/**
 * Field memory: state a field keeps with its form rather than with its component, so it survives the
 * field leaving the screen and coming back (a form in steps shows only the step on screen, a collapsed
 * section unmounts its fields). Bound to the form (its react-hook-form `control`), keyed by whatever the
 * field chooses (its own name, usually); generic, it knows no field type.
 *
 * A container that reuses its form for a new opening calls `resetFieldMemory(form)` when it resets it, so
 * nothing of the previous opening is remembered.
 */

/** What a form is identified by: its react-hook-form `control`, stable for the life of `useForm`. */
type FormLike = { control: object };

export type FieldMemory<T> = {
    /** The value remembered for this key, `undefined` when none (or since the last reset). */
    get(): T | undefined;
    set(value: T): void;
    clear(): void;
};

const memories = new WeakMap<object, Map<string, unknown>>();

function memoryOf(form: FormLike): Map<string, unknown> {
    let memory = memories.get(form.control);
    if (!memory) {
        memory = new Map();
        memories.set(form.control, memory);
    }
    return memory;
}

/**
 * The memory of `key` in `form`. Read on every access, so a reset done meanwhile (`resetFieldMemory`) is
 * seen by a component still mounted.
 */
export function fieldMemory<T>(form: FormLike, key: string): FieldMemory<T> {
    return {
        get: () => memoryOf(form).get(key) as T | undefined,
        set: value => {
            memoryOf(form).set(key, value);
        },
        clear: () => {
            memoryOf(form).delete(key);
        }
    };
}

/** Forgets everything the fields of `form` remembered: call it when the form is reset for a new opening. */
export function resetFieldMemory(form: FormLike): void {
    memories.delete(form.control);
}
